import PropTypes from "prop-types"
import React from "react"

import Divider from "src/components/Divider"
import LineItems from "src/components/LineItems/LineItems"

// Read-only reflection of what was requested at scheduling time — the boater
// and marina card/detail views this renders on offer no way to add, change,
// or remove an item. Settled 19 Aug 2026: a launch with no add-ons shows no
// section at all, not an empty one or a "None added" row.
//
// Nothing here is decided locally: addon.detail/amountFormatted come from
// DryStack::LaunchAddonDecorator, and the Subtotal/Tax/Estimated total block
// is handed to components/LineItems as ordered rows the same way the pre-submit
// modal renders it — the server owns the wording, order, and which rows are
// still partial.
//
// An estimate with an empty `rows` is a legitimate payload, not a broken one:
// a launch whose only add-on is a fill-up has nothing that can be totalled, so
// the server sends the items with no rows and no disclaimer (MOB-1080). The
// footer below is guarded on that, and the section still renders its heading
// and lines. What hasRequestedAddOns still guards is the other pairing —
// addons present with no estimate object at all — so a call site that wraps
// this component in its own divider/margin doesn't leave an empty wrapper
// behind. Exported so nobody has to re-derive it.
export const hasRequestedAddOns = (addons, addonEstimate) =>
  Boolean(addons?.length && addonEstimate?.rows)

// Shared with every call site (LaunchCard, LaunchShow, MarinaScheduleColumns)
// so the addon/estimate shape only has to change in one place when the
// serializer does — MOB-1054's fuel-tax field, for instance.
export const addonsPropType = PropTypes.arrayOf(
  PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    // "× 2", "× 5 gal", "— Fill tank" — rendered by
    // DryStack::LaunchAddonDecorator#detail, in the marina's own fuel unit.
    detail: PropTypes.string.isRequired,
    // "$4.99", "$4.459 / gal" for a pending fill-up — rendered by
    // DryStack::LaunchAddonDecorator#amount_formatted. Server-rendered because
    // a fill-up has no line total, only a per-unit price, so these rows can't
    // go through LineItems the way the estimate block below can.
    amountFormatted: PropTypes.string.isRequired,
  })
)

export const addonEstimatePropType = PropTypes.shape({
  heading: PropTypes.string,
  // Handed straight to components/LineItems: `type` selects the row component,
  // `amount` is raw minor units LineItems formats itself with `currency`, and
  // `suffix` is the server's pending-fuel note. The estimate serializer drops
  // amount_formatted and emphasis on purpose — `type` already tells LineItem
  // which component (and weight) to draw.
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
      suffix: PropTypes.string,
    })
  ),
  // Needed alongside amount: LineItems formats the raw minor units itself and
  // can't recover the marina's currency from an already-formatted string.
  currency: PropTypes.string,
  disclaimer: PropTypes.string,
})

const RequestedAddOns = ({ addons, addonEstimate }) => {
  if (!hasRequestedAddOns(addons, addonEstimate)) return null

  const renderItem = (addon) => (
    <div
      key={addon.id}
      className="flex items-center justify-between py-1 text-sm"
    >
      <span>
        <span>{addon.name}</span>
        <span className="text-muted"> {addon.detail}</span>
      </span>
      <span>{addon.amountFormatted}</span>
    </div>
  )

  // Owns its own top Divider so callers just render <RequestedAddOns /> and let
  // it decide whether anything shows — no caller repeats hasRequestedAddOns, and
  // none can leave an empty divider/wrapper behind (the bug on #11666). The one
  // exception is DataTableV2's card cell, which inspects the render's return
  // value and so still guards explicitly.
  return (
    <div>
      <Divider />
      <div className="font-semibold">{addonEstimate.heading}</div>
      <div className="divide-y">{addons.map(renderItem)}</div>
      {/* A launch with nothing priceable in it — a lone fill-up — comes back
          with no rows, and the whole footer goes with them rather than printing
          three zeroed lines. The server decides; see
          DryStack::LaunchAddonEstimateDecorator. */}
      {Boolean(addonEstimate.rows.length) && (
        <>
          <div className="mt-2 border-t pt-2 text-sm">
            <LineItems
              lineItems={addonEstimate.rows}
              currency={addonEstimate.currency}
              spacing={1}
            />
          </div>
          <p className="text-muted mt-1 text-xs italic">
            {addonEstimate.disclaimer}
          </p>
        </>
      )}
    </div>
  )
}

RequestedAddOns.propTypes = {
  addons: addonsPropType,
  addonEstimate: addonEstimatePropType,
}

export default RequestedAddOns
